import HttpService from './HttpService';

const formatError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.message || error.response?.data?.error || defaultMessage;
  const errorCode = error.response?.status;
  const details = error.response?.data?.details || {};

  if (process.env.NODE_ENV !== 'production') {
    console.error(defaultMessage, error);
  }

  return { message: errorMessage, code: errorCode, originalError: error, details };
};

// Statement (child) fields appended to the multipart form on create. Empty/blank
// values are skipped so the backend stores NULL rather than rejecting blank numerics.
const STATEMENT_FIELDS = [
  'statement_date',
  'due_date',
  'total_amount_due',
  'principal',
  'interest',
  'escrow',
  'fees',
  'additional_payment',
  'past_due_amount',
  'principal_balance',
  'escrow_balance',
  'interest_rate',
  'notes',
];

const MortgageService = {
  // ---------------------------- Mortgage (parent) ----------------------------

  /**
   * List mortgages for a house, each with statement_count + latest-statement figures.
   * @param {number} houseId
   * @returns {Promise<{mortgages: Array}>}
   */
  getMortgages: async (houseId) => {
    try {
      const response = await HttpService.get(`/houses/${houseId}/mortgages`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching mortgages');
    }
  },

  /** Get a single mortgage by id. */
  getMortgage: async (id) => {
    try {
      const response = await HttpService.get(`/mortgages/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching mortgage ${id}`);
    }
  },

  /** Create a mortgage (loan account) under a house. JSON body. */
  createMortgage: async (houseId, data) => {
    try {
      const response = await HttpService.post(`/houses/${houseId}/mortgages`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error creating mortgage');
    }
  },

  /** Update a mortgage's loan facts. JSON body. */
  updateMortgage: async (id, data) => {
    try {
      const response = await HttpService.put(`/mortgages/${id}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error updating mortgage ${id}`);
    }
  },

  /** Delete a mortgage (cascades to its statements + R2 files). */
  deleteMortgage: async (id) => {
    try {
      const response = await HttpService.delete(`/mortgages/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error deleting mortgage ${id}`);
    }
  },

  // ------------------------- MortgageStatement (child) -----------------------

  /** List statements for a mortgage. */
  getStatements: async (mortgageId) => {
    try {
      const response = await HttpService.get(`/mortgages/${mortgageId}/statements`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching mortgage statements');
    }
  },

  /** Get a single statement by id. */
  getStatement: async (id) => {
    try {
      const response = await HttpService.get(`/mortgage-statements/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching mortgage statement ${id}`);
    }
  },

  /**
   * Parse an uploaded statement file (PDF or image) and return its fields for
   * review — nothing is saved. Counts as one scan against the user's quota.
   * The returned `statement` object's keys line up with the statement form's
   * field names, so the caller can prefill directly.
   * @param {number} mortgageId
   * @param {File} file
   * @returns {Promise<{success: boolean, statement: Object}>}
   */
  extractStatement: async (mortgageId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await HttpService.post(`/mortgages/${mortgageId}/statements/extract`, formData);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error reading statement file');
    }
  },

  /**
   * Create a statement under a mortgage, optionally with an attached file (image or PDF).
   * @param {number} mortgageId
   * @param {Object} data field values plus an optional `file`
   */
  createStatement: async (mortgageId, data) => {
    try {
      const formData = new FormData();
      STATEMENT_FIELDS.forEach((field) => {
        const value = data[field];
        if (value !== undefined && value !== null && value !== '') {
          formData.append(field, String(value));
        }
      });
      if (data.file) formData.append('file', data.file);

      const response = await HttpService.post(`/mortgages/${mortgageId}/statements`, formData);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error creating mortgage statement');
    }
  },

  /**
   * Bulk import: parse AND create one statement from one file (ADR-0012).
   * Called once per file by the bulk-import runner; resolves for every
   * per-file outcome — read `outcome`, not the HTTP status. Only request-level
   * failures (404 / 503 / storage quota) reject. Does not use the scan quota.
   *
   * @param {number} mortgageId
   * @param {File} file
   * @param {{clientId?: string}} [opts]
   * @returns {Promise<{outcome: 'created'|'needs_attention'|'skipped_duplicate'|'failed',
   *   client_id: ?string, original_filename: ?string, parse_path: ?string,
   *   statement: ?Object, fields: ?Object, confidence: ?number, signals: string[],
   *   error: ?string, error_code: ?string, missing: ?string[], errors: ?Array,
   *   existing_statement_id: ?number}>}
   */
  importStatement: async (mortgageId, file, opts = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (opts.clientId) formData.append('client_id', opts.clientId);
      const response = await HttpService.post(`/mortgages/${mortgageId}/statements/import`, formData);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error importing statement');
    }
  },

  /** Update a statement's metadata (no file change). JSON body. */
  updateStatement: async (id, data) => {
    try {
      const response = await HttpService.put(`/mortgage-statements/${id}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error updating mortgage statement ${id}`);
    }
  },

  /** Delete a statement (and its attached R2 file). */
  deleteStatement: async (id) => {
    try {
      const response = await HttpService.delete(`/mortgage-statements/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error deleting mortgage statement ${id}`);
    }
  },

  // -------------------- MortgageEscrowDisclosure (child) ---------------------

  /** List a loan's escrow disclosures, newest escrow cycle first. */
  getEscrowDisclosures: async (mortgageId) => {
    try {
      const response = await HttpService.get(`/mortgages/${mortgageId}/escrow-disclosures`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching escrow disclosures');
    }
  },

  /**
   * Import one escrow disclosure from one file (ADR-0013): read AND create in
   * one pass, resolving for every per-file outcome like importStatement.
   * Passing `fields` skips the reader and saves those values with the file;
   * that is how the fix-it card saves a disclosure the reader couldn't finish.
   *
   * @param {number} mortgageId
   * @param {File} file
   * @param {{clientId?: string, fields?: Object}} [opts]
   * @returns {Promise<{outcome: 'created'|'needs_attention'|'skipped_duplicate'|'failed',
   *   client_id: ?string, original_filename: ?string, parse_path: ?string,
   *   disclosure: ?Object, fields: ?Object, confidence: ?number, signals: string[],
   *   error: ?string, error_code: ?string, missing: ?string[], errors: ?Array,
   *   existing_disclosure_id: ?number}>}
   */
  importEscrowDisclosure: async (mortgageId, file, opts = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (opts.clientId) formData.append('client_id', opts.clientId);
      if (opts.fields) formData.append('fields', JSON.stringify(opts.fields));
      const response = await HttpService.post(`/mortgages/${mortgageId}/escrow-disclosures/import`, formData);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error importing escrow disclosure');
    }
  },

  /** Delete an escrow disclosure (and its attached R2 file). */
  deleteEscrowDisclosure: async (id) => {
    try {
      const response = await HttpService.delete(`/mortgage-escrow-disclosures/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error deleting escrow disclosure ${id}`);
    }
  },
};

export default MortgageService;
